/**
 * DkimDetailPage — DKIM 密钥详情
 * 展示密钥信息；"查看 DNS 记录" + 删除操作放在此处（列表行不再直接提供）。
 */
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import { KeyRegular, CopyRegular, DeleteRegular } from '@fluentui/react-icons'
import { dkimApi } from '@/api/dkim'
import type { DkimKey } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate } from '@/lib/format'

const useStyles = makeStyles({
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  dialogKey: { color: tokens.colorNeutralForeground3, fontSize: '13px', marginBottom: '8px' },
  deleteName: { fontWeight: 600, color: tokens.colorStatusDangerForeground1 },
  dnsRow: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  dnsLabel: { fontSize: '13px', fontWeight: 600, color: tokens.colorNeutralForeground1 },
  dnsValue: {
    fontFamily: 'monospace',
    fontSize: '13px',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: 'break-all',
    whiteSpace: 'pre-wrap',
  },
})

export default function DkimDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { item, isLoading } = useDetailItem<DkimKey>(
    ['dkim'],
    () => dkimApi.list(),
    id as string,
    'id',
  )

  const stateItem = (location.state as { item?: DkimKey } | null)?.item

  const [dnsRecord, setDnsRecord] = useState<{ domain: string; dns_name: string; dns_record: string } | null>(null)
  const [dnsLoading, setDnsLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const openDns = () => {
    setDnsLoading(true)
    dkimApi
      .getDns(Number(id))
      .then(setDnsRecord)
      .catch((e) => {
        toast.error('读取失败', e instanceof ApiError ? e.message : '读取 DNS 记录失败')
      })
      .finally(() => setDnsLoading(false))
  }

  const deleteMutation = useMutation({
    mutationFn: () => dkimApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dkim'] })
      toast.success('删除成功', 'DKIM 密钥已删除')
      navigate(ROUTES.DKIM)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '删除失败'
      toast.error('删除失败', msg)
    },
  })

  return (
    <>
      <DetailLayout
        title={item?.domain ?? stateItem?.domain ?? 'DKIM 密钥'}
        subtitle={`Selector: ${item?.selector ?? stateItem?.selector}`}
        media={<KeyRegular fontSize={22} />}
        badge={
          <Badge appearance="filled" color={(item ?? stateItem)?.active ? 'success' : 'subtle'}>
            {(item ?? stateItem)?.active ? '启用' : '停用'}
          </Badge>
        }
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该 DKIM 密钥"
        actions={
          <>
            <Button
              appearance="subtle"
              icon={<KeyRegular />}
              disabled={dnsLoading}
              onClick={openDns}
            >
              查看 DNS 记录
            </Button>
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="密钥信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow label="域名" value={item?.domain ?? stateItem?.domain} />
          <DetailRow label="Selector" value={item?.selector ?? stateItem?.selector} />
          <DetailRow
            label="密钥长度"
            value={
              item?.key_size
                ? `${item.key_size} bit`
                : stateItem?.key_size
                  ? `${stateItem.key_size} bit`
                  : ''
            }
          />
          <DetailRow label="状态" value={(item ?? stateItem)?.active ? '启用' : '停用'} />
          <DetailRow label="创建时间" value={formatDate((item ?? stateItem)?.created_at)} />
        </DetailSection>
      </DetailLayout>

      {/* DNS 记录对话框 */}
      <Dialog open={dnsRecord !== null} onOpenChange={(_, d) => { if (!d.open) setDnsRecord(null) }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>DNS TXT 记录</DialogTitle>
            <DialogContent>
              {dnsRecord && (
                <div>
                  <Text className={styles.dialogKey}>
                    请在域名 <strong>{dnsRecord.domain}</strong> 的 DNS 中添加以下 TXT 记录：
                  </Text>
                  <div className={styles.dnsRow}>
                    <span className={styles.dnsLabel}>记录名（Host）</span>
                    <div className={styles.dnsValue}>{dnsRecord.dns_name}</div>
                  </div>
                  <div className={styles.dnsRow}>
                    <span className={styles.dnsLabel}>记录类型</span>
                    <div>TXT</div>
                  </div>
                  <div className={styles.dnsRow}>
                    <span className={styles.dnsLabel}>记录值（Value）</span>
                    <div className={styles.dnsValue}>{dnsRecord.dns_record}</div>
                  </div>
                  <Button
                    icon={<CopyRegular />}
                    onClick={() => {
                      navigator.clipboard.writeText(dnsRecord.dns_record)
                      toast.success('已复制', '记录值已复制到剪贴板')
                    }}
                  >
                    复制记录值
                  </Button>
                </div>
              )}
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button appearance="primary" onClick={() => setDnsRecord(null)}>关闭</Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认 */}
      <Dialog open={deleteOpen} onOpenChange={(_, d) => { if (!d.open) setDeleteOpen(false) }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定删除域 <span className={styles.deleteName}>{item?.domain ?? stateItem?.domain}</span> 的 DKIM 密钥吗？删除后出站邮件将不再签名，此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
